import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import OffersService, { ExperienceDealOptionsParams } from '~/services/OffersService';

const ALL_OPPORTUNITY_DEALS_FETCH_LIMIT = 500;
const ACCOUNT_OPPORTUNITIES_PAGE_LIMIT = 500;
const ACCOUNT_OFFER_IDS_PAGE_LIMIT = 100;

export const offerQueryKeys = {
  all: ['offers'] as const,
  details: () => [...offerQueryKeys.all, 'detail'] as const,
  detail: (id: string) => [...offerQueryKeys.details(), id] as const,
  opportunityDeals: (id: string | undefined) => [...offerQueryKeys.all, 'opportunity', id, 'deals'] as const,
  opportunityRelated: (id: string | undefined) => [...offerQueryKeys.all, 'opportunity', id, 'related'] as const,
  pricingToolDraft: (id: string | undefined) =>
    [...offerQueryKeys.all, 'opportunity', id, 'pricing-tool-draft'] as const,
  pricingToolSubmissions: (id: string | undefined) =>
    [...offerQueryKeys.all, 'opportunity', id, 'pricing-tool-submissions'] as const,
  opportunity: (id: string) => [...offerQueryKeys.all, 'opportunity', id] as const,
  opportunityDocuments: (id: string | undefined) => [...offerQueryKeys.all, 'opportunity', id, 'documents'] as const,
  accountOpportunities: (accountId: string | undefined) =>
    [...offerQueryKeys.all, 'opportunities', 'account', accountId] as const,
  account: (accountId: string | undefined) => [...offerQueryKeys.all, 'account', accountId] as const,
  accountOfferIds: (accountId: string | undefined) =>
    [...offerQueryKeys.all, 'account', accountId, 'offer-ids'] as const,
  accountAssets: (accountId: string | undefined, recordType: string) =>
    [...offerQueryKeys.all, 'account', accountId, 'assets', recordType] as const,
  experienceDealOptions: (params: ExperienceDealOptionsParams) =>
    [...offerQueryKeys.all, 'experience-deal-options', params] as const,
  campaignScheduleImportCandidates: (id: string | undefined) =>
    [...offerQueryKeys.all, id, 'campaign-schedules', 'import'] as const,
  scheduleLogs: (id: string | undefined) => [...offerQueryKeys.all, id, 'schedule-logs'] as const,
  crmResyncStatus: (id: string | undefined, requestedAt: string | undefined) =>
    [...offerQueryKeys.all, 'opportunity', id, 'crm-resync-status', requestedAt] as const,
};

export type OpportunityRoomAsset = {
  account__name?: string | null;
  accountid?: string | null;
  additional_bedding_information__c?: string | null;
  additional_bedding_type__c?: string | null;
  applies__c?: string | null;
  child_bedding__c?: string | null;
  cot_provided__c?: string | null;
  createddate?: string | null;
  createdbyid?: string | null;
  createdby__name?: string | null;
  current_opp__c?: string | null;
  currencyisocode?: string | null;
  deactivate__c?: boolean | null;
  description?: string | null;
  id?: string | number | null;
  inclusion_description__c?: string | null;
  included_number_of_adults__c?: string | number | null;
  included_number_of_children__c?: string | number | null;
  included_number_of_infant__c?: string | number | null;
  included_number_of_teenagers__c?: string | number | null;
  last_validated_on__c?: string | null;
  lastmodifieddate?: string | null;
  lastmodifiedbyid?: string | null;
  lastmodifiedby__name?: string | null;
  max_adults__c?: string | number | null;
  max_child_age__c?: string | null;
  max_children__c?: string | number | null;
  max_infant_age__c?: string | null;
  max_infants__c?: string | number | null;
  max_occupancy__c?: string | null;
  max_teenagers__c?: string | number | null;
  maximum_occupancy_number__c?: string | number | null;
  name?: string | null;
  number_of_rooms_connecting__c?: string | null;
  number_of_rooms_on_site__c?: string | null;
  opportunity__c?: string | null;
  prv__c?: string | number | null;
  rack_rate__c?: string | number | null;
  recordtypeid?: string | null;
  recordtype__name?: string | null;
  room_size__c?: string | number | null;
  rrp__c?: string | number | null;
  room_view__c?: string | null;
  sfid?: string | null;
  standard_bedding_configuration__c?: string | null;
  synced_at?: string | null;
  type__c?: string | null;
  valid_for__c?: string | null;
};

export type OpportunityDeal = {
  allocation__c?: string | number | null;
  bed_groups?: Array<{
    configuration?: Array<{ quantity?: number; type?: string }>;
  }>;
  id: string | number;
  converted_cost__c?: string | number | null;
  cost_price__c?: string | number | null;
  cost_price_currency_price__c?: string | number | null;
  currencyisocode?: string | null;
  extra_night_margin__c?: number | null;
  fk_room_type_id?: string | null;
  included_number_of_adults__c?: string | number | null;
  included_number_of_children__c?: string | number | null;
  included_number_of_infant__c?: string | number | null;
  included_number_of_teenagers__c?: string | number | null;
  inclusions_total_value__c?: string | number | null;
  margin_check__c?: string | null;
  margin_percent__c?: string | number | null;
  max_extra_nights__c?: string | number | null;
  max_room_pax__c?: string | number | null;
  name?: string | null;
  number_of_nights__c?: string | number | null;
  price__c?: string | number | null;
  room_asset?: OpportunityRoomAsset | null;
  room_size__c?: string | number | null;
  sfid?: string | null;
  suggested_value__c?: number | null;
  synced_at?: string | null;
};

export type OpportunityRoom = OpportunityDeal;

export function useGetOffer(offerId: string | undefined, options?: { retain_dynamic_tags: boolean }) {
  return useQuery({
    queryKey: options ? [...offerQueryKeys.detail(offerId), options] : offerQueryKeys.detail(offerId),
    queryFn: async () => {
      const response = await OffersService.getOffer(offerId, options);
      return response.result;
    },
    enabled: !!offerId,
  });
}

export function useGetOfferSchema(offerId: string | undefined) {
  return useQuery({
    queryKey: [...offerQueryKeys.detail(offerId), 'schema'],
    queryFn: async () => {
      const response = await OffersService.getOfferSchema(offerId);
      return response.patch.body.schema;
    },
    enabled: !!offerId,
  });
}

export function useGetRoomPolicySchema(offerId: string | undefined, { enabled = true }: { enabled?: boolean } = {}) {
  return useQuery({
    queryKey: [...offerQueryKeys.detail(offerId), 'room-policies', 'schema'],
    queryFn: async () => {
      const response = await OffersService.getRoomPolicySchema(offerId);
      return response.schema;
    },
    enabled: enabled && !!offerId,
  });
}

export function useGetOpportunity<TOpportunity = App.OpportunityPage>(
  opportunityId: string | undefined,
  { enabled = false }: { enabled?: boolean } = {},
) {
  return useQuery<TOpportunity>({
    queryKey: offerQueryKeys.opportunity(opportunityId ?? ''),
    queryFn: async () => {
      if (!opportunityId) {
        throw new Error('opportunityId is required');
      }

      const response = await OffersService.getOpportunity(opportunityId);
      return response.result as TOpportunity;
    },
    enabled: enabled && !!opportunityId,
  });
}

export function useGetAccountOpportunities(
  accountId: string | undefined,
  { enabled = true }: { enabled?: boolean } = {},
) {
  return useQuery({
    queryKey: offerQueryKeys.accountOpportunities(accountId),
    queryFn: async ({ signal }) => {
      if (!accountId) {
        throw new Error('accountId is required');
      }

      const opportunities: Array<App.OpportunityPage> = [];
      for (let page = 1; ; page++) {
        const response = await OffersService.getOpportunities(
          { vendorId: accountId, page, limit: ACCOUNT_OPPORTUNITIES_PAGE_LIMIT },
          { signal },
        );
        const { opportunities: pageOpportunities, total } = response.result as {
          opportunities: Array<App.OpportunityPage>;
          total: number;
        };
        opportunities.push(...pageOpportunities);
        if (pageOpportunities.length === 0 || opportunities.length >= total) {
          return opportunities;
        }
      }
    },
    enabled: enabled && !!accountId,
  });
}

export function useGetAccountOfferIds(accountId: string | undefined) {
  return useQuery({
    queryKey: offerQueryKeys.accountOfferIds(accountId),
    queryFn: async ({ signal }) => {
      const offerIds = new Set<string>();
      let fetched = 0;
      for (let page = 1; ; page++) {
        const { result, total } = await OffersService.getAccountOfferIds(accountId, {
          page,
          limit: ACCOUNT_OFFER_IDS_PAGE_LIMIT,
          signal,
        });
        result.forEach(({ id_salesforce_external }) => offerIds.add(id_salesforce_external));
        fetched += result.length;
        if (result.length === 0 || fetched >= total) {
          return offerIds;
        }
      }
    },
    enabled: !!accountId,
  });
}

export function useGetOpportunityDeals(
  opportunityId: string | undefined,
  { enabled = true }: { enabled?: boolean } = {},
) {
  return useQuery({
    queryKey: offerQueryKeys.opportunityDeals(opportunityId),
    queryFn: async () => {
      if (!opportunityId) {
        throw new Error('opportunityId is required');
      }

      const response = await OffersService.getDeals(opportunityId, 1, ALL_OPPORTUNITY_DEALS_FETCH_LIMIT);
      return response.result as Array<OpportunityDeal>;
    },
    enabled: enabled && !!opportunityId,
  });
}

export function useUpdateOffer(offerId: string, options?: { invalidateOnSuccess?: boolean }) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data: Record<string, unknown>) => {
      return OffersService.updateOffer(data, offerId);
    },
    onSuccess: () => {
      const shouldInvalidate = options?.invalidateOnSuccess ?? true;

      if (!shouldInvalidate) {
        return;
      }

      queryClient.invalidateQueries({ queryKey: offerQueryKeys.detail(offerId) });
    },
  });
}

export interface StructuredFinePrint {
  cancellation_text: string;
  date_changes_text: string;
  inclusion_notes: string;
  fine_print: string;
}

export interface StructuredFacilityItem {
  code: string;
  name: string;
  icon?: string | null;
  displayOrder?: number | null;
}

export interface StructuredFacilityGroup {
  group: {
    code: string;
    name: string;
    icon?: string | null;
    displayOrder?: number | null;
  };
  facilities: Array<StructuredFacilityItem>;
}

export function useUpdateStructuredFinePrint(offerId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data: StructuredFinePrint) => {
      return OffersService.updateOffer({ structured_fine_print: data }, offerId);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: offerQueryKeys.detail(offerId) });
    },
  });
}
